import { Spacer } from '@odekoteam/doppio';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';

import { GET_WAREHOUSE_PICKUP_PREFILL } from 'src/graphql/requests/getWarehouseThirdParty';
import { Route } from 'src/graphql/types/delivery';
import {
  GGetWarehousePickupPrefillQuery,
  GProviderDispatchStatus,
  GThirdPartyProvider,
} from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { useFocusTrap } from 'src/hooks/useFocusTrap';
import {
  nightEndSelector,
  routesSelector,
  selectedWarehouseIdSelector,
} from 'src/state/selectors/delivery';
import { modalSelector } from 'src/state/selectors/modal';
import { cancelProviderRoute } from 'src/state/slices/delivery/cancelProviderRoute';
import { dispatchProviderRoute } from 'src/state/slices/delivery/dispatchProviderRoute';
import {
  markRouteForProvider,
  ProviderRouteResult,
} from 'src/state/slices/delivery/markRouteForProvider';
import { refetchRouteList } from 'src/state/slices/delivery/refetchRouteList';
import { requoteProviderRoute } from 'src/state/slices/delivery/requoteProviderRoute';
import { unmarkRouteProvider } from 'src/state/slices/delivery/unmarkRouteProvider';
import { hideModal } from 'src/state/slices/modal';
import { nameStopsInMessage } from 'src/utils/burqRoute';
import { getModifiedIsoDate, todayYmd } from 'src/utils/date';
import {
  BURQ_TAB_HEADING_ID,
  focusWhenPresent,
  ROUTE_DETAILS_TITLE_ID,
} from 'src/utils/focus';
import {
  cheapestQuote,
  isLiveDispatchStatus,
  isProviderRedeliveryRoute,
  ProviderQuote,
  providerDisplayName,
} from 'src/utils/thirdPartyDelivery';
import {
  DialogView,
  dialogViewFor,
  isCancelConfirmable,
  isLiveStatus,
} from './MarkRouteForProvider/dialogView';
import { PickupTimeFields } from './MarkRouteForProvider/PickupTimeFields';
import {
  PolledRoute,
  useProviderDispatchPoll,
} from './MarkRouteForProvider/useProviderDispatchPoll';
import {
  CANCEL_ON_PROVIDER_ID,
  cancelConfirm,
  errorTextSx,
  KEEP_BUTTON_ID,
  keepConfirm,
  viewContent,
} from './MarkRouteForProvider/views';
import { buttonContainer, styledConfirmationModal } from './styles';

// Wraps, so a full-width primary (primaryRowSx) takes the first row alone.
export const actionRowSx = {
  ...buttonContainer,
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  gap: '8px',
} as const;

type MarkRouteForProviderVariables = {
  routeId: string;
  routeName: string;
  // The route's night, an ISO date ("2026-11-04"); the pickup falls on it,
  // or on today once it has passed (pickupDay), or today for an escalation.
  nightEnd: string;
  provider: GThirdPartyProvider;
  warehouseId: string;
  timeZone: string;
  returnFocusId?: string;
  // Where focus goes after Keep with Odeko, when the opener is gone with the
  // route's provider: the route panel's title unless the opener says otherwise.
  keepFocusId?: string;
  // Where focus goes after a dispatch, when the GM is not on the delivery
  // page whose Burq sub-tab the dispatch lands on (the Issues page).
  dispatchedFocusId?: string;
  // Opened from a "Cancel on Burq" control: the dialog shows the cancel
  // confirm over the running route, then follows the provider's answer.
  openOn?: 'cancel';
};

const TITLE_ID = 'mark-route-for-provider-title';

// LDS treats a dispatch claim older than this as stale and reconciles it
// against the provider before dispatching again (route_dispatch_service.rb).
export const DISPATCH_CLAIM_STALE_MS = 90 * 1000;

const PAST_PICKUP = 'pickup time is in the past';
// LDS's refusal for an unknown or expired quote ends with this.
const REQUOTE_HINT = /request quotes again\.?$/i;

// The pickup as an instant: the given day at the GM's HH:MM in the
// warehouse's own zone, whatever zone the browser is in. A DST change cannot
// move the local date, so LDS's day check holds.
export const pickupInstant = (
  nightEnd: string,
  time: string,
  timeZone: string,
): Date =>
  zonedTimeToUtc(`${getModifiedIsoDate(nightEnd)}T${time}:00`, timeZone);

// The day the courier dialog's pickup falls on: the route's night, or today
// in the warehouse's zone once that night has passed. LDS takes the night for
// a GM's route, which is never marked on a past night, and any day from today
// to the night for an escalation (ThirdParty::PickupTime), so a past-night
// escalation is collected today. Both are YYYY-MM-DD, so they compare as text.
export const pickupDay = (
  nightEnd: string,
  timeZone: string,
  today: string = todayYmd(timeZone),
): string => {
  const night = getModifiedIsoDate(nightEnd);
  return night < today ? today : night;
};

// The dialog's own request freezes the view; its success waits on a fresh
// answer; otherwise the poll decides.
const shownView = (
  frozen: { view: DialogView } | null,
  isPending: boolean,
  derived: DialogView,
): DialogView => {
  if (frozen) return frozen.view;
  return isPending ? { kind: 'loading' } : derived;
};

const countStops = (route: PolledRoute | undefined): number =>
  (route?.stops?.length ?? 0) + (route?.redeliveryStops?.length ?? 0);

// How long a message outlives the request that set it: until the poll's fresh
// answer, until quotes replace the refused ones, until the view it was
// written for is gone, or until the dispatch it speaks about moves on.
type MessageUntil =
  | { kind: 'answer' }
  | { kind: 'freshQuotes'; quoteIds: string }
  | { kind: 'viewLeft'; view: DialogView['kind'] }
  | { kind: 'recordMoved'; at: DispatchState }
  | null;

// What a cancel refusal speaks about: the dispatch's status, its claim and its
// error. A courier assigned moves none of them.
type DispatchState = { status: unknown; claim: unknown; error: unknown };

const dispatchState = (route: PolledRoute | undefined): DispatchState => ({
  status: route?.providerDispatch?.status ?? null,
  claim: route?.providerDispatch?.cancelRequestedAt ?? null,
  error: route?.providerDispatch?.error ?? null,
});

// Burq answered what the message was about: the status or the error changed,
// or a claim that stood was cleared or replaced. A claim appearing is the
// request itself landing, not an answer.
const dispatchMoved = (at: DispatchState, now: DispatchState): boolean =>
  now.status !== at.status ||
  now.error !== at.error ||
  (at.claim !== null && now.claim !== at.claim);

// The views a refused quote's reason stays on while its requote runs.
const QUOTE_FLOW_VIEWS: DialogView['kind'][] = ['quotes', 'waiting', 'loading'];

const outlived = (
  until: MessageUntil,
  now: { view: DialogView['kind']; quoteIds: string; record: DispatchState },
): boolean => {
  if (until?.kind === 'viewLeft') return now.view !== until.view;
  if (until?.kind === 'recordMoved') return dispatchMoved(until.at, now.record);
  if (until?.kind !== 'freshQuotes') return false;
  const replaced = now.view === 'quotes' && now.quoteIds !== until.quoteIds;
  return replaced || !QUOTE_FLOW_VIEWS.includes(now.view);
};

type HeaderRoute = {
  thirdPartyRoute?: boolean | null;
  thirdPartyProvider?: string | null;
  providerDispatch?: {
    status: GProviderDispatchStatus;
    error?: string | null;
    cancelRequestedAt?: unknown;
    cancelable?: boolean;
    unmarkable?: boolean;
  } | null;
};

// What the route header renders for a route: whether a provider has it, which
// one, its dispatch's status and error, and which way back it offers.
const headerKey = (route: HeaderRoute | undefined): string =>
  JSON.stringify([
    !!route?.thirdPartyRoute,
    route?.thirdPartyProvider ?? null,
    route?.providerDispatch?.status ?? null,
    route?.providerDispatch?.error ?? null,
    route?.providerDispatch?.cancelRequestedAt ?? null,
    !!route?.providerDispatch?.cancelable,
    !!route?.providerDispatch?.unmarkable,
  ]);

const listedRoute = (
  routes: ({ id: string } & HeaderRoute)[] | undefined,
  routeId: string,
): HeaderRoute | undefined => routes?.find(({ id }) => id === routeId);

const isPast = (iso: string | null | undefined): boolean =>
  !!iso && Date.parse(iso) < Date.now();

// A cancel claim, compared by value: a cancel leaves the status alone.
const claimOf = (route: PolledRoute | undefined): unknown =>
  route?.providerDispatch?.cancelRequestedAt ?? null;

const isBackWithOdeko = (view: DialogView): boolean =>
  view.kind === 'ended' && view.reason === 'backWithOdeko';

// A refusal LDS also wrote to the dispatch shows once, in the body.
const visibleMessage = (
  message: string | undefined,
  recordError: string | null | undefined,
): string | undefined =>
  message && message !== recordError ? message : undefined;

// What the dialog shows: a confirm standing in for the view, or the view.
const contentFor = ({
  viewed,
  keep,
  cancel,
  routeName,
  providerName,
  isSubmitting,
  liveDispatch,
}: {
  viewed: ReturnType<typeof viewContent>;
  keep: { open: boolean; onRemove: () => Promise<void>; onBack: () => void };
  cancel: { open: boolean; onSend: () => Promise<void>; onBack: () => void };
  routeName: string;
  providerName: string;
  isSubmitting: boolean;
  liveDispatch: boolean;
}): ReturnType<typeof viewContent> => {
  const ctx = { routeName, providerName, isSubmitting };
  if (keep.open)
    return keepConfirm({ ...ctx, liveDispatch }, keep.onRemove, keep.onBack);
  if (cancel.open) return cancelConfirm(ctx, cancel.onSend, cancel.onBack);
  return viewed;
};

// Snapshotted at open: hideModal clears the store's variables while this
// dialog may still render once, and the route, warehouse and zone must stay the
// ones the GM saw when they chose the provider.
export function MarkRouteForProviderModal(): JSX.Element | null {
  const current: MarkRouteForProviderVariables | undefined =
    useAppSelector(modalSelector).variables;
  const [snapshot] = useState(current);
  return snapshot ? <MarkRouteForProviderDialog {...snapshot} /> : null;
}

// The courier dialog: sends the route, waits for quotes, dispatches against
// one. Its view follows the route's newest dispatch, read by a poll, with two
// rules for the dialog's own requests: while one is in flight the view does
// not move, and after one succeeds the dialog waits for a fresh answer rather
// than trusting a poll that may have left before the request did.
function MarkRouteForProviderDialog({
  routeId,
  routeName,
  nightEnd,
  provider,
  warehouseId,
  timeZone,
  returnFocusId,
  keepFocusId = ROUTE_DETAILS_TITLE_ID,
  dispatchedFocusId = BURQ_TAB_HEADING_ID,
  openOn,
}: MarkRouteForProviderVariables): JSX.Element {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const viewingNight = useAppSelector(nightEndSelector).viewing;
  const viewingWarehouseId = useAppSelector(selectedWarehouseIdSelector);
  const providerName = providerDisplayName(provider);
  // Refetch what the GM is looking at now, which may not be the night the
  // route belongs to. A ref, so `close` stays stable for the focus trap.
  const routeList = useRef({ warehouseId, nightEnd });
  routeList.current = {
    warehouseId: viewingWarehouseId ?? warehouseId,
    nightEnd: viewingNight ?? nightEnd,
  };

  // The form's pickup time.
  const [time, setTime] = useState('');
  const [asap, setAsap] = useState(false);
  const [touched, setTouched] = useState(false);
  // Change: the pickup time for a requote.
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeTime, setChangeTime] = useState('');
  const [changeAsap, setChangeAsap] = useState(false);

  // Every write names how long the message lasts; none outlives its reason.
  const [message, setMessageText] = useState<string | undefined>();
  const messageUntil = useRef<MessageUntil>(null);
  const setMessage = useCallback(
    (text: string | undefined, until: MessageUntil = null): void => {
      messageUntil.current = until;
      setMessageText(text);
    },
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmKeep, setConfirmKeep] = useState(false);
  // The cancel confirm. Armed at open by a "Cancel on Burq" opener, so the GM
  // confirms once; it shows only over a view LDS would cancel from.
  const [confirmCancel, setConfirmCancel] = useState(openOn === 'cancel');
  // Back or Escape on a confirm the dialog opened on closes the dialog.
  const armedAtOpen = useRef(openOn === 'cancel');
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>();
  // The quote the GM last dispatched against, for Dispatch again.
  const [heldQuote, setHeldQuote] = useState<ProviderQuote>();
  const [frozen, setFrozen] = useState<{
    view: DialogView;
    route: PolledRoute | undefined;
  } | null>(null);
  const [pending, setPending] = useState<{
    status: GProviderDispatchStatus | undefined;
    // A cancel leaves the status alone and sets its claim.
    claim: unknown;
    revision: number;
  } | null>(null);
  const [claimStale, setClaimStale] = useState(false);
  const [ended, setEnded] = useState(false);

  const seenLive = useRef(false);
  // Whether the dialog sent anything, so Close refreshes the route list now
  // rather than on the next Burq poll.
  const sentRequest = useRef(false);

  // network-only: the last time used changes with every mark, and the cached
  // answer would be the one from before this GM's previous mark.
  const [{ data: prefillData, fetching: prefillFetching }] =
    useQuery<GGetWarehousePickupPrefillQuery>({
      query: GET_WAREHOUSE_PICKUP_PREFILL,
      variables: { warehouseId, provider },
      pause: !warehouseId,
      requestPolicy: 'network-only',
    });
  const prefill = prefillData?.warehouse?.lastThirdPartyPickupTime;

  // Never over a time the GM has already typed, cleared, or swapped for ASAP.
  useEffect(() => {
    if (!touched && prefill) setTime(prefill);
  }, [prefill, touched]);

  const poll = useProviderDispatchPoll(routeId, { active: !ended });
  const derived = dialogViewFor({
    route: poll.route,
    pollFailed: !!poll.error,
    superseded: poll.superseded,
    seenLive: seenLive.current,
    openOn,
  });
  useEffect(() => {
    setEnded(derived.kind === 'ended');
  }, [derived.kind]);

  const status = poll.route?.providerDispatch?.status;
  // What the header shows for this route, to tell whether Close must refresh it.
  const routes = useAppSelector(routesSelector);
  const headerStale = useRef(false);
  headerStale.current =
    !!poll.route &&
    headerKey(poll.route) !== headerKey(listedRoute(routes, routeId));
  // Read when a request lands, not when its handler was made: the poll keeps
  // answering while a request is in flight.
  const revisionNow = useRef(poll.revision);
  revisionNow.current = poll.revision;
  const statusAtSend = useRef(status);
  const claim = claimOf(poll.route);
  const claimAtSend = useRef<unknown>(claim);
  useEffect(() => {
    if (!poll.route) return;
    if (status && isLiveStatus(status)) seenLive.current = true;
  }, [poll.route, status]);

  // Leave the pending view on a fresh answer that moved, or on the second
  // fresh answer, which left after the request landed. An end state the poll
  // already read is no stale answer, and the poll has stopped on it: no fresh
  // answer is coming, so it ends the wait.
  // A message that speaks for a wait goes with it: a lost response's note
  // when the fresh answer lands, a refused quote's reason when quotes that
  // replace it are on screen or the dialog has left the quotes.
  const derivedEnded = derived.kind === 'ended';
  useEffect(() => {
    if (!pending) return;
    const answered =
      derivedEnded ||
      (poll.revision > pending.revision &&
        (status !== pending.status ||
          claim !== pending.claim ||
          poll.revision >= pending.revision + 2));
    if (!answered) return;
    setPending(null);
    if (messageUntil.current?.kind === 'answer') setMessage(undefined);
  }, [pending, poll.revision, status, claim, derivedEnded, setMessage]);

  const isPending = !frozen && !!pending;
  const shown = shownView(frozen, isPending, derived);
  const shownRoute = frozen ? frozen.route : poll.route;
  const shownRecord = shownRoute?.providerDispatch;
  const quotes = shownRecord?.quotes ?? [];

  // A selection that is gone after a requote resets to the cheapest.
  const quoteIds = quotes.map(({ id }) => id).join(',');
  // The dispatch as the poll last read it, whatever view is frozen. Compared
  // by value: each poll answer is a new object.
  const record = dispatchState(poll.route);
  const recordNow = useRef(record);
  recordNow.current = record;
  const recordKey = JSON.stringify(record);
  useEffect(() => {
    if (
      outlived(messageUntil.current, {
        view: shown.kind,
        quoteIds,
        record: recordNow.current,
      })
    ) {
      setMessage(undefined);
    }
  }, [quoteIds, shown.kind, recordKey, setMessage]);
  useEffect(() => {
    if (!selectedQuoteId || !quotes.some(({ id }) => id === selectedQuoteId)) {
      setSelectedQuoteId(cheapestQuote(quotes)?.id);
    }
    // quoteIds stands for the list's content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteIds]);

  // A dispatch claim LDS has held this long may be stuck; it reconciles a
  // second request against the provider and dispatches at most once.
  const dispatching = derived.kind === 'dispatching';
  useEffect(() => {
    setClaimStale(false);
    if (!dispatching) return undefined;
    const timer = setTimeout(
      () => setClaimStale(true),
      DISPATCH_CLAIM_STALE_MS,
    );
    return () => clearTimeout(timer);
  }, [dispatching]);

  // Escape and Close are ignored mid-request: a refusal is shown here and
  // nowhere else. Refs, so `cancel` stays stable for the focus trap.
  const submitting = useRef(false);
  // A route back with Odeko has lost the control that opened this dialog.
  const backWithOdeko = useRef(false);
  backWithOdeko.current = isBackWithOdeko(shown);
  const close = useCallback((): void => {
    if (submitting.current) return;
    dispatch(hideModal());
    // Bring the header and the route list up to what this saw, without
    // waiting for the next poll.
    if (sentRequest.current || headerStale.current) {
      refetchRouteList(dispatch, routeList.current);
    }
    if (backWithOdeko.current) {
      focusWhenPresent(keepFocusId);
      return;
    }
    if (returnFocusId) document.getElementById(returnFocusId)?.focus();
  }, [dispatch, returnFocusId, keepFocusId]);
  const confirmKeepOpen = useRef(false);
  confirmKeepOpen.current = confirmKeep;
  const backToKeep = useRef(false);
  const cancelConfirmShown = confirmCancel && isCancelConfirmable(shown);
  const confirmCancelOpen = useRef(false);
  confirmCancelOpen.current = cancelConfirmShown;
  const backToCancel = useRef(false);
  // Back from the cancel confirm: to the view it stands over, or, when the
  // dialog opened on it, to wherever the GM came from.
  const backFromCancel = useCallback((): void => {
    if (submitting.current) return;
    if (armedAtOpen.current) {
      armedAtOpen.current = false;
      close();
      return;
    }
    backToCancel.current = true;
    setConfirmCancel(false);
  }, [close]);
  const cancel = useCallback((): void => {
    if (submitting.current) return;
    // Escape inside the Keep with Odeko confirm closes only the confirm.
    if (confirmKeepOpen.current) {
      backToKeep.current = true;
      setConfirmKeep(false);
      return;
    }
    if (confirmCancelOpen.current) {
      backFromCancel();
      return;
    }
    close();
  }, [close, backFromCancel]);
  useFocusTrap(ref, { isOpen: true, onClose: cancel });

  // The confirm answers the view it was opened from; a view the poll moved
  // on from takes it down.
  useEffect(() => {
    setConfirmKeep(false);
  }, [shown.kind]);
  // The cancel confirm survives the first answer (loading to running) and
  // goes once the view is one LDS would not cancel from.
  const cancelConfirmable = isCancelConfirmable(shown);
  useEffect(() => {
    if (shown.kind === 'loading' || cancelConfirmable) return;
    armedAtOpen.current = false;
    setConfirmCancel(false);
  }, [shown.kind, cancelConfirmable]);

  // Every view change moves focus to its heading: the trap focuses only once.
  // A confirm the GM cancelled hands focus back to the button that opened it.
  const viewKey = `${shown.kind}:${changeOpen}:${confirmKeep}:${cancelConfirmShown}`;
  const firstView = useRef(true);
  useEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    let target = TITLE_ID;
    if (backToKeep.current) target = KEEP_BUTTON_ID;
    if (backToCancel.current) target = CANCEL_ON_PROVIDER_ID;
    backToKeep.current = false;
    backToCancel.current = false;
    document.getElementById(target)?.focus();
  }, [viewKey]);

  // Runs one of the dialog's own requests with the view frozen on the one
  // that sent it.
  const run = async <T,>(request: () => Promise<T>): Promise<T> => {
    setMessage(undefined);
    setIsSubmitting(true);
    submitting.current = true;
    setFrozen({ view: shown, route: shownRoute });
    statusAtSend.current = status;
    claimAtSend.current = claim;
    sentRequest.current = true;
    try {
      return await request();
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
      setFrozen(null);
    }
  };

  // Answers counted from the moment the request landed: one that left while
  // it was in flight may still show the status from before it.
  const waitForAnswer = (): void => {
    setPending({
      status: statusAtSend.current,
      claim: claimAtSend.current,
      revision: revisionNow.current,
    });
    poll.refetch();
  };

  const mark = async (pickupAt: string | undefined): Promise<void> => {
    // Retired before the send: a poll answer during the request may already
    // carry the new provider route id, which is ours, not another session's.
    poll.clearPin();
    const sentFrom = shown.kind;
    const result = await run(async () => {
      const action = await dispatch(
        markRouteForProvider({
          routeId,
          provider,
          pickupAt,
          ...routeList.current,
        }),
      );
      return action.payload as ProviderRouteResult | undefined;
    });
    if (result?.ok) {
      waitForAnswer();
      return;
    }
    // No message means the response was lost: the send may have gone through,
    // and the route list was refreshed to show which. Either way the poll
    // answers it: the note stays only while the view it was sent from does.
    setMessage(
      result?.message ??
        'Could not confirm the send. Check the route status before trying again.',
      { kind: 'viewLeft', view: sentFrom },
    );
  };

  // An escalation is collected today, the day the Escalate dialog sent it
  // for, whatever night it sits on; a GM's route on its night (pickupDay).
  // The route list stands in until the poll has read the route.
  const shownPickupDay = (): string => {
    const known = shownRoute ?? routes?.find(({ id }) => id === routeId);
    return known && isProviderRedeliveryRoute(known)
      ? todayYmd(timeZone)
      : pickupDay(nightEnd, timeZone);
  };

  const markFromForm = async (): Promise<void> => {
    if (submitting.current) return;
    let pickupAt: string | undefined;
    if (!asap) {
      const instant = pickupInstant(shownPickupDay(), time, timeZone);
      if (instant.getTime() < Date.now()) {
        setMessage(PAST_PICKUP);
        return;
      }
      pickupAt = instant.toISOString();
    }
    await mark(pickupAt);
  };

  const openChange = (error?: string): void => {
    const current = shownRecord?.pickupAt;
    setChangeAsap(!current);
    setChangeTime(current ? formatInTimeZone(current, timeZone, 'HH:mm') : '');
    setChangeOpen(true);
    setMessage(error);
  };

  // Every requote names its time: LDS overwrites the pickup with whatever a
  // requote carries, so leaving it out would turn a set time into ASAP.
  // Resolves true once LDS accepted it.
  const requote = async (pickupAt: string | null): Promise<boolean> => {
    if (submitting.current) return false;
    if (isPast(pickupAt)) {
      openChange(PAST_PICKUP);
      return false;
    }
    const sentFrom = shown.kind;
    const result = await run(async () => {
      const action = await dispatch(
        requoteProviderRoute({ routeId, pickupAt, ...routeList.current }),
      );
      return action.payload as ProviderRouteResult | undefined;
    });
    if (result?.ok) {
      setChangeOpen(false);
      waitForAnswer();
      return true;
    }
    if (result?.message) {
      setMessage(result.message, { kind: 'viewLeft', view: sentFrom });
      return false;
    }
    setMessage(
      'Could not confirm the request for new quotes. This updates on its own.',
      { kind: 'answer' },
    );
    waitForAnswer();
    return false;
  };
  const requoteAsBefore = (): Promise<boolean> =>
    requote(shownRecord?.pickupAt ?? null);

  const requoteFromChange = async (): Promise<void> => {
    if (changeAsap) {
      await requote(null);
      return;
    }
    const instant = pickupInstant(shownPickupDay(), changeTime, timeZone);
    if (instant.getTime() < Date.now()) {
      setMessage(PAST_PICKUP);
      return;
    }
    await requote(instant.toISOString());
  };

  const dispatchQuote = async (quote: ProviderQuote): Promise<void> => {
    if (submitting.current) return;
    setHeldQuote(quote);
    const sentFrom = shown.kind;
    const result = await run(async () => {
      const action = await dispatch(
        dispatchProviderRoute({
          routeId,
          routeName,
          provider,
          quote,
          ...routeList.current,
        }),
      );
      return action.payload as ProviderRouteResult | undefined;
    });
    if (result?.ok) {
      dispatch(hideModal());
      // On the delivery page the dispatch landed the GM on the Burq sub-tab.
      focusWhenPresent(dispatchedFocusId);
      return;
    }
    if (!result?.message) {
      setMessage('Could not confirm the dispatch. This updates on its own.', {
        kind: 'answer',
      });
      waitForAnswer();
      return;
    }
    // An unknown or expired quote: ask for fresh ones at the same time, and
    // keep the reason on screen while they arrive. A requote that was not
    // sent shows its own reason instead (a past pickup, a refusal).
    if (!REQUOTE_HINT.test(result.message)) {
      setMessage(result.message, { kind: 'viewLeft', view: sentFrom });
      return;
    }
    const refusedIds = quoteIds;
    if (await requoteAsBefore()) {
      setMessage(result.message, { kind: 'freshQuotes', quoteIds: refusedIds });
    }
  };

  const keepWithOdeko = async (): Promise<void> => {
    if (submitting.current) return;
    const removed = await run(async () => {
      const action = await dispatch(
        unmarkRouteProvider({ routeId, ...routeList.current }),
      );
      return action.payload === true;
    });
    // A refusal is toasted by the thunk; the dialog stays.
    if (!removed) {
      setConfirmKeep(false);
      return;
    }
    dispatch(hideModal());
    // The opener is gone once the route is an Odeko route again.
    focusWhenPresent(keepFocusId);
  };

  // Sends the cancel from its confirm. Success means the request is out; the
  // poll brings the answer. A refusal's reason stays past the view it was sent
  // from (a courier assigned moves the route out of the running view, and the
  // reason must still show) until the dispatch moves on: Burq's later answer
  // to a request that timed out replaces it.
  const cancelOnProvider = async (): Promise<void> => {
    if (submitting.current) return;
    const result = await run(async () => {
      const action = await dispatch(
        cancelProviderRoute({ routeId, ...routeList.current }),
      );
      return action.payload as ProviderRouteResult | undefined;
    });
    armedAtOpen.current = false;
    setConfirmCancel(false);
    if (result?.ok) {
      waitForAnswer();
      return;
    }
    if (result?.message) {
      const listed = (routes as Route[] | undefined)?.find(
        ({ id }) => id === routeId,
      );
      setMessage(nameStopsInMessage(result.message, listed), {
        kind: 'recordMoved',
        at: recordNow.current,
      });
      poll.refetch();
      return;
    }
    setMessage('Could not confirm the cancel. This updates on its own.', {
      kind: 'answer',
    });
    waitForAnswer();
  };

  const stopCount = countStops(shownRoute);
  const canMark = !prefillFetching && !isSubmitting && (asap || time !== '');
  const changeNeedsTime = !changeAsap && changeTime === '';

  const viewed = viewContent(shown, changeOpen, {
    routeName,
    providerName,
    timeZone,
    isSubmitting,
    isPending,
    stillWaiting: poll.stillWaiting,
    claimStale,
    record: shownRecord ?? undefined,
    stopCount,
    selectedQuoteId,
    heldQuote,
    canMark,
    formFields: (
      <PickupTimeFields
        time={time}
        asap={asap}
        needsTime={!prefillFetching && !asap && time === ''}
        onTimeChange={(value): void => {
          setTouched(true);
          setMessage(undefined);
          setTime(value);
        }}
        onAsapChange={(value): void => {
          setTouched(true);
          setMessage(undefined);
          setAsap(value);
        }}
      />
    ),
    changeNeedsTime,
    changeFields: (
      <PickupTimeFields
        time={changeTime}
        asap={changeAsap}
        needsTime={changeNeedsTime}
        onTimeChange={(value): void => {
          setMessage(undefined);
          setChangeTime(value);
        }}
        onAsapChange={(value): void => {
          setMessage(undefined);
          setChangeAsap(value);
        }}
      />
    ),
    onClose: close,
    onMark: markFromForm,
    onRequote: requoteAsBefore,
    onRequoteFromChange: requoteFromChange,
    onBackFromChange: (): void => {
      setMessage(undefined);
      setChangeOpen(false);
    },
    onDispatch: dispatchQuote,
    onOpenChange: (): void => openChange(),
    onSelectQuote: setSelectedQuoteId,
    onKeep: (): void => setConfirmKeep(true),
    onCancelOnProvider: (): void => setConfirmCancel(true),
    openedToCancel: openOn === 'cancel',
  });
  const content = contentFor({
    viewed,
    keep: { open: confirmKeep, onRemove: keepWithOdeko, onBack: cancel },
    cancel: {
      open: cancelConfirmShown,
      onSend: cancelOnProvider,
      onBack: backFromCancel,
    },
    routeName,
    providerName,
    isSubmitting,
    liveDispatch: isLiveDispatchStatus(shownRecord?.status),
  });
  const shownMessage = visibleMessage(message, shownRecord?.error);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      id="mark-route-for-provider-modal"
      sx={{ ...styledConfirmationModal, textAlign: 'left', maxWidth: '440px' }}
    >
      <h2
        id={TITLE_ID}
        tabIndex={-1}
        sx={{ variant: 'text.headingSm', margin: 0 }}
      >
        {content.heading}
      </h2>
      <Spacer height="$3" />
      {content.body}
      {poll.error && poll.route && (
        <span sx={errorTextSx}>Updates paused, retrying.</span>
      )}
      <span
        aria-live="polite"
        sx={{
          ...errorTextSx,
          color: shownMessage ? 'errorText' : 'secondaryText',
        }}
      >
        {shownMessage ?? content.statusLine}
      </span>
      <div sx={actionRowSx}>{content.actions}</div>
    </div>
  );
}
